import { DynamicDialogConfig } from 'primeng/dynamicdialog';

/**
 * Helper class for creating consistent dialog configurations across the application.
 * All dialogs should use modal: true to prevent interaction with background elements.
 */
export class DialogHelper {
  private static readonly MOBILE_BREAKPOINT = '1023px';
  private static readonly RESPONSIVE_CLASS = 'eko-responsive-dialog';

  /**
   * Creates a default dialog configuration with modal enabled and backdrop blocking.
   */
  static createBaseConfig<T = unknown>(config?: Partial<DynamicDialogConfig<T>>): DynamicDialogConfig<T> {
    return {
      modal: true,
      dismissableMask: false,
      closeOnEscape: true,
      ...config,
    };
  }

  static mergeClasses(...classes: (string | undefined)[]): string {
    return classes.filter(Boolean).join(' ').trim();
  }

  static withResponsiveShell<T = unknown>(
    width: string,
    config?: Partial<DynamicDialogConfig<T>>,
    options?: { contentScroll?: boolean },
  ): DynamicDialogConfig<T> {
    const contentScroll = options?.contentScroll !== false;
    const { styleClass, contentStyle, breakpoints, width: _width, ...rest } = config ?? {};

    return this.createBaseConfig({
      width,
      styleClass: this.mergeClasses(this.RESPONSIVE_CLASS, styleClass),
      breakpoints: breakpoints ?? { [this.MOBILE_BREAKPOINT]: '100vw' },
      ...(contentScroll
        ? { contentStyle: { maxHeight: '85vh', overflow: 'auto', ...contentStyle } }
        : { contentStyle }),
      ...rest,
    });
  }

  /**
   * Responsive dialog with optional desktop width (default 70vw).
   */
  static createConfig<T = unknown>(config?: Partial<DynamicDialogConfig<T>>): DynamicDialogConfig<T> {
    const width = config?.width ?? '70vw';
    const { width: _width, ...rest } = config ?? {};
    return this.withResponsiveShell(width, rest);
  }

  static createLargeConfig<T = unknown>(config?: Partial<DynamicDialogConfig<T>>): DynamicDialogConfig<T> {
    return this.withResponsiveShell('90vw', { maximizable: true, ...config });
  }

  static createMediumConfig<T = unknown>(config?: Partial<DynamicDialogConfig<T>>): DynamicDialogConfig<T> {
    return this.withResponsiveShell('70vw', config);
  }

  static createSmallConfig<T = unknown>(config?: Partial<DynamicDialogConfig<T>>): DynamicDialogConfig<T> {
    return this.withResponsiveShell('50vw', config);
  }

  static createNarrowConfig<T = unknown>(config?: Partial<DynamicDialogConfig<T>>): DynamicDialogConfig<T> {
    return this.withResponsiveShell('500px', config, { contentScroll: false });
  }

  static createPackagingEntryConfig<T = unknown>(config?: Partial<DynamicDialogConfig<T>>): DynamicDialogConfig<T> {
    return this.withResponsiveShell('70vw', {
      styleClass: 'packaging-entry-dialog',
      ...config,
    });
  }

  static createRegisterCardConfig<T = unknown>(config?: Partial<DynamicDialogConfig<T>>): DynamicDialogConfig<T> {
    return this.withResponsiveShell('60vw', {
      styleClass: 'register-card-dialog',
      maximizable: true,
      ...config,
    });
  }
}
